import { UseMutationResult } from '@tanstack/react-query';
import { UploadedFileInfo } from 'hooks/mutations/useFileUploadMutation';
import { useCallback, useEffect, useRef, useState } from 'react';

type UseDocumentUploadParams = {
  watchedFormField: { file: File } | undefined;
  fileUploadMutation: UseMutationResult<{ data: UploadedFileInfo }, unknown, FormData>;
};

export type UseDocumentUploadReturn = {
  file: UploadedFileInfo | null;
  setFile: (file: UploadedFileInfo | null) => void;
  isReplacing: boolean;
  setIsReplacing: (replacing: boolean) => void;
  reset: () => void;
};

export function useDocumentUpload({ watchedFormField, fileUploadMutation }: UseDocumentUploadParams): UseDocumentUploadReturn {
  const [file, setFile] = useState<UploadedFileInfo | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const mutateRef = useRef(fileUploadMutation.mutateAsync);
  mutateRef.current = fileUploadMutation.mutateAsync;

  useEffect(() => {
    if (watchedFormField) {
      const formData = new FormData();
      formData.append('file', watchedFormField.file);
      (async () => {
        const fileData = await mutateRef.current(formData);
        setFile(fileData?.data);
      })();
    } else {
      setFile(null);
    }
  }, [watchedFormField]);

  const reset = useCallback(() => {
    setFile(null);
    setIsReplacing(false);
  }, []);

  return { file, setFile, isReplacing, setIsReplacing, reset };
}
